/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getCurrentCategory, CurrentCategoryInput } from '@msdyn365-commerce-modules/retail-actions';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';

export class GetCategoryByIdInput implements Msdyn365.IActionInput {
    public categoryId: number;
    constructor(categoryId: number) {
        this.categoryId = categoryId;
    }

    public getCacheKey = () => `GETCATEGORYBYID-${this.categoryId}`;
    public getCacheObjectType = () => 'GETCATEGORYBYID';
}

export interface IGetCategoryByIdData {
    currentCategory: CategoryHierarchy | null;
    categoriesFamily: CategoryHierarchy[];
    parentCategory: CategoryHierarchy | null;
}

const EMPTY_CATEGORY_NAVIGATION: IGetCategoryByIdData = {
    currentCategory: null,
    parentCategory: null,
    categoriesFamily: []
};

const createInput = (categoryId: number): Msdyn365.IActionInput => {
    return new GetCategoryByIdInput(categoryId);
};

async function action(_input: CurrentCategoryInput, ctx: Msdyn365.IActionContext): Promise<IGetCategoryByIdData> {
    try {
        const currentCategoryInput = new CurrentCategoryInput(ctx.requestContext, true);
        const currentCategory = await getCurrentCategory(currentCategoryInput, ctx);

        if (!currentCategory) {
            // Reaches App Insights `traces` at severity=Error.
            const recordId = ctx.requestContext.urlTokens?.recordId ?? 'unknown';
            ctx.telemetry.error(`[get-category-by-id] currentCategory not resolved for recordId=${recordId}`);
            return EMPTY_CATEGORY_NAVIGATION;
        }

        const currentParentCategoryInput = new CurrentCategoryInput(ctx.requestContext, true);
        currentParentCategoryInput.categoryId = currentCategory.ParentCategory;
        const parentCategory = await getCurrentCategory(currentParentCategoryInput, ctx);

        return {
            currentCategory,
            parentCategory: parentCategory ?? null,
            categoriesFamily: currentCategory?.Children || parentCategory?.Children || []
        };
    } catch (error) {
        const wrapped = error instanceof Error ? error : new Error(`[get-category-by-id] failed: ${String(error)}`);
        ctx.telemetry.error(`[get-category-by-id] Failed to fetch category navigation: ${wrapped.message}`);
        ctx.telemetry.exception(wrapped);
        return EMPTY_CATEGORY_NAVIGATION;
    }
}

export default Msdyn365.createObservableDataAction({
    id: 'GETCATEGORYBYID',
    input: args => {
        const categoryId = args.requestContext.query.categoryId ? parseInt(args.requestContext.query.categoryId.toString(), 10) : 0;
        return createInput(categoryId);
    },
    action: <Msdyn365.IAction<IGetCategoryByIdData>>action
});
